import React from 'react';
import { Query, Mutation } from '@apollo/client/react/components';
import { mapAfter } from 'apollo-relay/update-query';
import Styled from 'styled';

import LoadingProgress from 'lib/react-router/loading-progress';
import observe from 'lib/intersection-observer/observe';
import { RouteWithSearchRenderProps } from 'lib/react-router/route-with-search';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';

import gaTrack from 'four-nets/lib/google-analytics/track';
import ErrorPage from 'four-nets/errors/error-page';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import { FIRST, PAGE_SIZE, AUTO_LOAD_COUNT } from 'four-nets/photoblog/feed/feed-loader';
import Create from 'four-nets/photoblog/create-post/create';
import OffsetPager from 'four-nets/ui-v2/pager/offset-pager';

import query, { GroupFeedLoaderQuery, GroupFeedLoaderQueryVariables } from './group-feed-loader.graphql';
import PlaceholderPost from 'four-nets/photoblog/posts/feed-post/placeholder-post';

import Feed from 'four-nets/photoblog/feed/feed';

import mutation, { SetLastVisitMutation, SetLastVisitMutationVariables } from './mutations/set-last-visit.graphql';
import defaultStyles from './group-feed-loader.scss';

interface Params {
    slug: string;
    imageId: string;
}

interface Search {
    search?: string;
    offset?: number;
}

interface Props extends RouteWithSearchRenderProps<Params, Search> {
    id: string;
    h?: string;
    styles: typeof defaultStyles;
}

interface State {
    loading?: boolean;
    visible: boolean;
    autoLoadCount: number;
}

class GroupFeedLoader extends React.PureComponent<Props, State> {
    fetchMore?: () => Promise<any> = undefined;
    unobserve?: () => void;
    ref: React.RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);
        this.ref = React.createRef();
        this.state = { visible: false, autoLoadCount: AUTO_LOAD_COUNT - 1 };
    }

    componentDidMount() {
        const element = this.ref && (this.ref.current as Element | null);
        if (element) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '300px' }
            );
        }
    }

    componentWillUnmount() {
        if (this.unobserve) {
            this.unobserve();
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const element = this.ref && (this.ref.current as Element | null);
        if (element && !this.unobserve) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '300px' }
            );
        }

        /* If we already loaded the page for X times, we unobserve the fetchMore button  */
        let autoLoadActive = true;
        if (prevState.autoLoadCount > 0 && this.state.autoLoadCount <= 0) {
            if (this.unobserve) {
                this.unobserve();
            }
            autoLoadActive = false;
        }

        /* When the fetchMore button appeared, we want to fetchMore if autoload is still active */
        const appeared = this.state.visible && !prevState.visible;
        if (autoLoadActive && appeared && !this.state.loading && this.fetchMore) {
            this.fetchMore();
            this.setState(prevState => ({ visible: false, autoLoadCount: prevState.autoLoadCount - 1 }));
        }

        const offsetChange = prevProps.search.offset !== this.props.search.offset;
        if (offsetChange) {
            this.setState({ autoLoadCount: AUTO_LOAD_COUNT - 1 });
        }
    }

    render() {
        const {
            id,
            h,
            styles,
            match,
            location: { pathname },
            search: { search: searchQueryparam, offset },
        } = this.props;

        const { slug, imageId } = match.params;
        const { autoLoadCount } = this.state;

        let after: string | undefined;
        if (offset) {
            after = btoa(`arrayconnection:${offset - 1}`) || undefined;
        }

        return (
            <ViewerConsumer>
                {({ viewer }) =>
                    !viewer ? (
                        <Unauthorized />
                    ) : (
                        <React.Fragment>
                            {slug ? null : (
                                <div className={styles.createWrapper}>
                                    <Create
                                        style={{
                                            container: styles.container,
                                            contentWrapper: styles.contentWrapper,
                                            footer: styles.footer,
                                        }}
                                        groupId={id}
                                        refetchQueries={[
                                            {
                                                query,
                                                variables: {
                                                    id,
                                                    search: searchQueryparam,
                                                    first: FIRST,
                                                    slug,
                                                    hashtag: h,
                                                    after,
                                                },
                                            },
                                        ]}
                                    />
                                </div>
                            )}
                            <Mutation<SetLastVisitMutation, SetLastVisitMutationVariables> mutation={mutation}>
                                {setLastVisit => (
                                    <Query<GroupFeedLoaderQuery, GroupFeedLoaderQueryVariables>
                                        query={query}
                                        variables={
                                            {
                                                id,
                                                first: FIRST,
                                                search: searchQueryparam,
                                                slug,
                                                hashtag: h,
                                                after,
                                            } as GroupFeedLoaderQueryVariables
                                        }
                                        notifyOnNetworkStatusChange={true}
                                        fetchPolicy='cache-first'
                                        onCompleted={() => {
                                            setLastVisit({ variables: { input: { groupId: id } } });
                                        }}
                                    >
                                        {({ error, data, loading, refetch, fetchMore }) => {
                                            const endCursor =
                                                data &&
                                                data.group &&
                                                data.group.feed &&
                                                data.group.feed.pageInfo.endCursor;

                                            if (
                                                data &&
                                                data.group &&
                                                data.group.feed &&
                                                data.group.feed.pageInfo &&
                                                data.group.feed.pageInfo.hasNextPage
                                            ) {
                                                this.fetchMore = () => {
                                                    this.setState({ loading: true });

                                                    return fetchMore({
                                                        variables: { after: endCursor },
                                                        updateQuery: (
                                                            previousResult = { group: {} } as GroupFeedLoaderQuery,
                                                            { fetchMoreResult = { group: {} } as GroupFeedLoaderQuery }
                                                        ) => {
                                                            return {
                                                                ...previousResult,
                                                                ...fetchMoreResult,
                                                                group: {
                                                                    ...previousResult.group!,
                                                                    ...fetchMoreResult.group!,
                                                                    feed: mapAfter(
                                                                        previousResult!.group!.feed,
                                                                        fetchMoreResult!.group!.feed
                                                                    ),
                                                                },
                                                            };
                                                        },
                                                    }).finally(() => this.setState({ loading: false }));
                                                };
                                            } else {
                                                this.fetchMore = undefined;
                                            }

                                            let nextOffset;
                                            const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
                                                endCursor,
                                                pathname,
                                                pageSize: FIRST,
                                            });

                                            if (endCursor) {
                                                nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
                                            } else {
                                                nextOffset = offset;
                                            }

                                            return (
                                                <ErrorPage error={error}>
                                                    <LoadingProgress loading={loading}>
                                                        <Feed
                                                            loading={loading}
                                                            data={data && data.group}
                                                            refetch={refetch}
                                                            imageId={imageId}
                                                            nonMainFeed={true}
                                                            showFeedAds={true}
                                                        />
                                                        {this.state.loading
                                                            ? [null, null, null, null].map((_, index) => (
                                                                  <PlaceholderPost key={index} />
                                                              ))
                                                            : null}
                                                        <div ref={this.ref as any} className={styles.fetchMoreWrapper}>
                                                            {this.fetchMore ? (
                                                                <FetchMoreButton
                                                                    disabled={!this.fetchMore || this.state.loading}
                                                                    loading={this.state.loading}
                                                                    href={endCursorHref}
                                                                    prevHref={prevCursorHref}
                                                                    onClick={() => {
                                                                        if (this.fetchMore) {
                                                                            this.fetchMore().then(() => {
                                                                                this.setState(prevState => ({
                                                                                    visible: false,
                                                                                    autoLoadCount:
                                                                                        prevState.autoLoadCount - 1,
                                                                                }));
                                                                            });

                                                                            if (typeof window !== 'undefined') {
                                                                                gaTrack('pageview', endCursorHref);
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            ) : null}
                                                            {(data &&
                                                                data.group &&
                                                                data.group.feed &&
                                                                data.group.feed.edges!.length !== 0 &&
                                                                data.group.feed.edges!.length % PAGE_SIZE === 0) ||
                                                            autoLoadCount <= 0 ? (
                                                                <OffsetPager pageSize={PAGE_SIZE} offset={nextOffset} />
                                                            ) : null}
                                                        </div>
                                                    </LoadingProgress>
                                                </ErrorPage>
                                            );
                                        }}
                                    </Query>
                                )}
                            </Mutation>
                        </React.Fragment>
                    )
                }
            </ViewerConsumer>
        );
    }
}

export default Styled(defaultStyles)(GroupFeedLoader);
